import type { NotificationResponse, NotificationType } from '@shared/schemas';

/**
 * The notification panel's rules, kept out of the component so they can be
 * tested without rendering it: no JSX, no MUI, no router here.
 */

export type NotificationTab = 'all' | 'unread';

export interface NotificationGroup {
  label: 'Today' | 'Earlier';
  items: NotificationResponse[];
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * When a notification arrived, in words that cannot be misread.
 *
 * Today's notifications get a relative time; older ones a written-out date. The
 * shared `timeAgo` falls back to `12/06/2026` after 30 days (12 June or
 * 6 December depending on who reads it) so it is not used here.
 *
 * The cut is the calendar day, not 24 hours, so that the label always agrees
 * with the Today / Earlier group the notification sits in.
 */
export function notificationWhen(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);

  if (isSameDay(date, now)) {
    const mins = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    return `${Math.floor(mins / 60)}h ago`;
  }

  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Yesterday';

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * Splits the list into Today and Earlier, keeping the server's order (newest
 * first). An empty group is left out rather than shown with no rows under it.
 */
export function groupNotifications(
  notifications: NotificationResponse[],
  now: Date = new Date(),
): NotificationGroup[] {
  const today: NotificationResponse[] = [];
  const earlier: NotificationResponse[] = [];
  for (const n of notifications) {
    (isSameDay(new Date(n.created_at), now) ? today : earlier).push(n);
  }
  const groups: NotificationGroup[] = [];
  if (today.length > 0) groups.push({ label: 'Today', items: today });
  if (earlier.length > 0) groups.push({ label: 'Earlier', items: earlier });
  return groups;
}

/**
 * The verb that tells the reader where a click on the notification leads.
 *
 * It names the page the notification's `redirection_url` actually opens, not a
 * nicer one: a new list notification opens that list since #645 ("Open the
 * list"), while older ones still lead to the lists page ("View lists"). `null`
 * when the destination is not worth announcing (the welcome notification only
 * goes back to the dashboard).
 */
export function notificationAction(
  type: NotificationType,
  redirectionUrl: string | null,
): string | null {
  switch (type) {
    case 'cookie_expiry':
      return 'Reconnect';
    case 'new_list':
      return /^\/lists\/[^/?#]+/.test(redirectionUrl ?? '') ? 'Open the list' : 'View lists';
    case 'invitation_accepted':
      return 'View licence';
    case 'enrichment_started':
    case 'enrichment_completed':
    case 'enrichment_failed':
      return 'View pipeline';
    case 'list_feedback':
      return 'Open the feedback';
    case 'feedback_reply':
      return 'Open the comments';
    case 'client_import':
      return 'Open the import';
    case 'welcome':
      return null;
  }
}

/**
 * Notifications that block something until the reader acts: collection stops
 * with an expired LinkedIn session, and a failed enrichment leaves profiles
 * unprocessed. They carry an "Action required" label, not just a colour.
 */
export function isActionRequired(type: NotificationType): boolean {
  return type === 'cookie_expiry' || type === 'enrichment_failed';
}
